import React, { useEffect, useState } from 'react';
import { useLoadingScreen } from '../../../../hooks/useLoadingScreen';
import style from './RoleManagementEdit.module.scss';
import {
  ButtonPrimary,
  Label,
  ModalContent,
  ModalHeader,
  SelectInputSearch,
} from '@skf/bt-wind-rail-commonlibrary';
import {
  RoleManagementEditRoleRoute,
  RoleManagementRoute,
  UserManagementRoute,
} from '../../../../routes/routes';
import { usePathname } from '../../../../hooks/usePathname';
import { IRolesState } from '../../../../store/roles/store';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../../store/store';
import { deleteRole, fetchRoles, fetchRolesInfo, putRole } from '../../../../store/roles/actions';
import { Role } from '../../../../store/roles/types';
import { useToast } from '../../../../hooks/useToast';
import { getPriorityOptions, getUserWebRolesOptions } from '../RoleManagement.mapper';
import RoleManagementAddDataTable from '../add/dataTable/RoleManagementAddDataTable';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { FullscreenModal } from '../../../common/fullscreenModal/FullscreenModal';
import { useTranslation } from 'react-i18next';
import { IUserSettingsState } from '../../../../store/userSettings/store';
import { isUserAuthorized } from '../../../../services/authentication/authenticationService';
import { UserWebFeatures } from '../../../../store/userSettings/types';

interface IUserManagementAddProps {}

const RoleManagementEdit: React.FunctionComponent<IUserManagementAddProps> = () => {
  const { setLoading } = useLoadingScreen();
  const { setPathname } = usePathname();
  const { addToast } = useToast();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const userSettings: IUserSettingsState = useSelector<
    StoreState,
    IUserSettingsState
  >((state) => state.userSettings);

  useEffect(() => {
    const authorized = isUserAuthorized(userSettings, [
      UserWebFeatures.ROLE_MANAGEMENT_ACCESS,
    ]);
    if (authorized) setPathname(UserManagementRoute.path);
  }, [userSettings, userSettings.isFetching]);

  const { params } = useRouteParams<{ roleName?: string }>(RoleManagementEditRoleRoute);

  const [currentRole, setCurrentRole] = useState<Role>();
  const [selectedUserWebRole, setSelectedUserWebRole] = useState<string>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isDMOpen, setDMOpen] = useState<boolean>(false);

  const { rolesInfo, status, roles }: IRolesState = useSelector<StoreState, IRolesState>((state) =>  state.roles);

  const getStatusMessage1 = (_status: number): string => {
    switch (_status) {
      case 200:
        return 'Role saved successfully';
      case 405:
        return 'Role is in use, cannot be updated';
      default:
        return `Error ${_status}`;
    }
  };
  const getStatusMessage2 = (_status: number): string => {
    switch (_status) {
      case 200:
        return 'Role deleted successfully';
      case 405:
        return 'Role is in use, cannot be deleted';
      default:
        return `Error ${_status}`;
    }
  };

  useEffect(() => {
    if(!params) return;
    if(!roles) {
      dispatch(fetchRoles());
      return;
    }
    const targetRole = roles.find(role => role.name === decodeURI(params?.roleName));
    if(!targetRole)
      return setPathname(RoleManagementRoute.path);

    setCurrentRole(targetRole);
  }, [roles, params]);

  useEffect(() => {
    if(!rolesInfo)
      dispatch(fetchRolesInfo());
  }, [rolesInfo]);

  useEffect(() => {
    if(!rolesInfo || !roles) return;
    setLoading(false);
  }, [roles, rolesInfo]);

  useEffect(() => {
    if((!isSubmitted && !isDeleting) || !status) return;
    const isGood = status === 200;

    if(isSubmitted) {
      addToast({
        message: getStatusMessage1(status),
        status: isGood ? 'ok' : 'danger'
      });
    } else if(isDeleting) {
      addToast({
        message: getStatusMessage2(status),
        status: isGood ? 'ok' : 'danger'
      });
    }

    setIsSubmitted(false);
    setLoading(false);

    setPathname(RoleManagementRoute.path);
  }, [status]);

  useEffect(() => {
    setCurrentRole({
      userWebRoles: [],
      name: '',
      industryName: undefined,
      priority: undefined
    });
  }, []);

  const onChangePriority = (option: any) => {
    if(!currentRole) return;
    setCurrentRole({
      ...currentRole,
      priority: option?.value
    });
  };

  const onChangeUserWebRole = (option: any) => {
    setSelectedUserWebRole(option?.value);
  };

  const onClickAddUserWebRole = () => {
    if(!currentRole || !selectedUserWebRole) return;
    setCurrentRole({
      ...currentRole,
      userWebRoles: [...currentRole.userWebRoles, selectedUserWebRole]
    });
    setSelectedUserWebRole(undefined);
  };

  const onRemoveUserWebRole = (userWebRole: string) => {
    if(!currentRole) return;
    setCurrentRole({
      ...currentRole,
      userWebRoles: currentRole.userWebRoles.filter(_userWebRole => userWebRole !== _userWebRole)
    });
  };

  const onClickCancel = () => {
    setPathname(RoleManagementRoute.path);
  };

  const onCloseDeleteModal = () => {
    setDMOpen(false);
  };

  const onClickSave = () => {
    setIsSubmitted(true);
    setLoading(true);
    setTimeout(() => {
      dispatch(putRole(currentRole));
    }, 10);
  };

  const onDelete = () => {
    console.log('delete');
    setIsDeleting(true);
    setLoading(true);
    setTimeout(() => {
      dispatch(deleteRole(currentRole));
    }, 10);
  };

  const canUpdate = currentRole
    && currentRole.name.length > 0
    && currentRole.industryName
    && currentRole.priority !== undefined
    && currentRole.userWebRoles.length > 0
  ;

  return (
    <>
      {
        isDMOpen && (
          <FullscreenModal
            onClose={onCloseDeleteModal}
            className={style.modal}
          >
            <ModalHeader
              onClose={onCloseDeleteModal}
            >Delete role</ModalHeader>
            <ModalContent className={style.content}>
              <div className={style.text}>
               {t('You_are_trying_to_delete')}<Label className={style.label}>{currentRole?.name}</Label> {t('are_you_sure')}?
              </div>
              <div className={style.navigation}>
                <ButtonPrimary onClick={onCloseDeleteModal} outline>Cancel</ButtonPrimary>
                <ButtonPrimary onClick={onDelete}>Delete</ButtonPrimary>
              </div>
            </ModalContent>
          </FullscreenModal>
        )
      }
      <div className={style.form}>
        <h3>Role Info</h3>
        <div className={style.firstSection}>
          <Label className={style.label}>
            {currentRole?.name}
          </Label>
          <Label className={style.label}>
            {currentRole?.industryName}
          </Label>
          <Label className={style.label}>
            {currentRole?.btPortalIndustry}
          </Label>
          <SelectInputSearch
            placeHolder={'Priority'}
            options={getPriorityOptions(rolesInfo, currentRole?.industryName)}
            value={currentRole?.priority + ''}
            isDisabled={currentRole?.industryName === undefined}
            onSelectedItemChanged={onChangePriority}
          />
        </div>
        <h3>User Web Roles</h3>
        <div className={style.secondSection}>
          <div className={style.header}>
            <SelectInputSearch
              placeHolder={'User Web Roles'}
              options={getUserWebRolesOptions(rolesInfo, currentRole?.industryName, currentRole?.btPortalIndustry, currentRole?.userWebRoles)}
              value={selectedUserWebRole}
              isDisabled={currentRole?.industryName === undefined}
              onSelectedItemChanged={onChangeUserWebRole}
            />
            <ButtonPrimary
              disabled={selectedUserWebRole === undefined}
              onClick={onClickAddUserWebRole}
            >Add User Web Role</ButtonPrimary>
          </div>
          <div>
            <RoleManagementAddDataTable
              userWebRoles={currentRole?.userWebRoles || []}
              onRemove={onRemoveUserWebRole}
            />
          </div>
        </div>
        <div>
          <div className={style.navigation}>
            <ButtonPrimary
              onClick={() => setDMOpen(true)}
              outline
            >Delete</ButtonPrimary>
            <div/>
            <ButtonPrimary
              onClick={onClickCancel}
              outline
            >Cancel</ButtonPrimary>
            <ButtonPrimary
              onClick={onClickSave}
              disabled={!canUpdate}
            >Update</ButtonPrimary>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleManagementEdit;
